import SelectBox from 'devextreme-react/select-box';
import type { ChartThemeColors } from '../../charts/useChartThemeColors';
import {
  DayOfWeekChart,
  DurationHistogramChart,
  HourOfDayChart,
  MultiSeriesTrendChart,
  WeeklyTrendBarChart
} from '../userActivityCharts';
import type { UserActivityAnalyticsTrendsSection, UserActivityReportMetric } from '../types';
import type { UserActivityTrendsSubview } from '../userActivityPrefs';
import { ReportChartCard } from './ReportChartCard';

const SUBVIEW_OPTIONS: { value: UserActivityTrendsSubview; text: string }[] = [
  { value: 'daily', text: 'Daily' },
  { value: 'weekly', text: 'Weekly' },
  { value: 'hourOfDay', text: 'Hour of day' },
  { value: 'dayOfWeek', text: 'Day of week' },
  { value: 'durationBands', text: 'Duration bands' }
];

const METRIC_OPTIONS: { value: UserActivityReportMetric; text: string }[] = [
  { value: 'sessions', text: 'Sessions' },
  { value: 'uniqueUsers', text: 'Unique users' },
  { value: 'totalMinutes', text: 'Total minutes' }
];

export function TrendsReport({
  trends,
  reportingTimeZone,
  chartColors,
  subview,
  onSubviewChange,
  metric,
  onMetricChange
}: {
  trends: UserActivityAnalyticsTrendsSection;
  reportingTimeZone: string;
  chartColors: ChartThemeColors;
  subview: UserActivityTrendsSubview;
  onSubviewChange: (next: UserActivityTrendsSubview) => void;
  metric: UserActivityReportMetric;
  onMetricChange: (next: UserActivityReportMetric) => void;
}) {
  const showMetric = subview === 'hourOfDay' || subview === 'dayOfWeek';

  return (
    <div className="user-activity-analytics__body user-activity-analytics__body--chart">
      <div className="user-activity-analytics__chart-header">
        <div>
          <h3 className="ops-section-title" style={{ margin: 0 }}>
            Trends
          </h3>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--ops-color-muted, #6b7280)', fontSize: 14 }}>
            Buckets use {reportingTimeZone}. Weeks are Sunday–Saturday. Duration bands use completed
            sessions only.
          </p>
        </div>
        <div className="user-activity-report-card__toolbar">
          <SelectBox
            dataSource={SUBVIEW_OPTIONS}
            value={subview}
            valueExpr="value"
            displayExpr="text"
            width={160}
            onValueChanged={(e) => onSubviewChange(e.value as UserActivityTrendsSubview)}
          />
          {showMetric ? (
            <SelectBox
              dataSource={METRIC_OPTIONS}
              value={metric}
              valueExpr="value"
              displayExpr="text"
              width={150}
              onValueChanged={(e) => onMetricChange(e.value as UserActivityReportMetric)}
            />
          ) : null}
        </div>
      </div>

      {trends.dailyTrendCapped ? (
        <p className="user-activity-report-banner">
          Daily/weekly series capped to the latest 366 days for all-time or very long ranges.
        </p>
      ) : null}

      {subview === 'daily' ? (
        <ReportChartCard
          title="Daily trend"
          subtitle="Sessions, unique users, and total minutes per day."
          empty={trends.daily.every((d) => d.sessions === 0)}
        >
          <div className="user-activity-analytics__chart-host user-activity-analytics__chart-host--fill">
            <MultiSeriesTrendChart data={trends.daily} colors={chartColors} />
          </div>
        </ReportChartCard>
      ) : null}

      {subview === 'weekly' ? (
        <ReportChartCard
          title="Weekly trend"
          subtitle="Sunday–Saturday weeks intersecting the selected range."
          empty={trends.weekly.every((d) => d.sessions === 0)}
        >
          <div className="user-activity-analytics__chart-host user-activity-analytics__chart-host--fill">
            <WeeklyTrendBarChart data={trends.weekly} colors={chartColors} />
          </div>
        </ReportChartCard>
      ) : null}

      {subview === 'hourOfDay' ? (
        <ReportChartCard
          title="Hour of day"
          subtitle="Session starts by local reporting hour."
          empty={trends.hourOfDay.every((d) => d.sessions === 0)}
        >
          <div className="user-activity-analytics__chart-host user-activity-analytics__chart-host--fill">
            <HourOfDayChart data={trends.hourOfDay} colors={chartColors} metric={metric} />
          </div>
        </ReportChartCard>
      ) : null}

      {subview === 'dayOfWeek' ? (
        <ReportChartCard
          title="Day of week"
          subtitle="Session starts by weekday in the reporting timezone."
          empty={trends.dayOfWeek.every((d) => d.sessions === 0)}
        >
          <div className="user-activity-analytics__chart-host user-activity-analytics__chart-host--fill">
            <DayOfWeekChart data={trends.dayOfWeek} colors={chartColors} metric={metric} />
          </div>
        </ReportChartCard>
      ) : null}

      {subview === 'durationBands' ? (
        <ReportChartCard
          title="Duration bands"
          subtitle="Completed session length distribution. Open sessions are excluded."
          empty={trends.durationBands.every((d) => d.sessions === 0)}
        >
          <div className="user-activity-analytics__chart-host user-activity-analytics__chart-host--fill">
            <DurationHistogramChart data={trends.durationBands} colors={chartColors} />
          </div>
        </ReportChartCard>
      ) : null}
    </div>
  );
}
